// src/features/alerts/AlertsService.js
import { customFetch } from '../../customFetch';
import { DOCUMENTS, INSPECTIONS } from '../../utils/URLS';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
dayjs.extend(utc);

/**
 * Servicio para gestionar las operaciones relacionadas con vencimientos y alertas
 */
const AlertsService = {
  /**
   * Obtener todos los vencimientos de documentos e inspecciones
   * @returns {Promise<Object>} Objeto con documents e inspections filtrados
   */
  async getAllExpirations() {
    try {
      // Realizar ambas peticiones en paralelo
      const [documentsResponse, inspectionsResponse] = await Promise.all([
        customFetch(DOCUMENTS.EXPIRATIONS, {}),
        customFetch(INSPECTIONS.EXPIRATIONS, {})
      ]);

      // Convertir a JSON
      const documentsExpirations = await documentsResponse.json();
      const inspectionsExpirations = await inspectionsResponse.json();

      // ✅ FILTRAR documentos con validación estricta
      const validDocuments = documentsExpirations.filter(document => {
        // Verificar que person existe y tiene datos válidos
        const hasValidPerson = document.person && 
          document.person !== null && 
          (document.person.id || document.person.firstName || document.person.businessName);
        
        // Verificar que work existe y tiene datos válidos  
        const hasValidWork = document.work && 
          document.work !== null && 
          (document.work.id || document.work.name);
        
        // Debe tener al menos una relación válida
        const isValid = hasValidPerson || hasValidWork;
        

        
        return isValid;
      });

      // ✅ FILTRAR inspecciones - REQUIERE assignedTool válido SIEMPRE
      const validInspections = inspectionsExpirations.filter(inspection => {
        // ✅ OBLIGATORIO: Debe tener assignedTool válido
        const hasValidAssignedTool = inspection.assignedTool && 
          inspection.assignedTool !== null && 
          inspection.assignedTool.id && 
          inspection.assignedTool.tool && 
          inspection.assignedTool.tool.name;
        
        if (!hasValidAssignedTool) {

          return false;
        }
        
        return true;
      });

      // Procesar los documentos válidos
      const documentsData = validDocuments.map(document => {
        // Marcar como documento para identificarlo después
        document.document = true;

        // Formatear fecha de vencimiento
        if (document.endDate) {
          document.date = document.endDate;

          // Calcular tipo de alerta basado en la diferencia de tiempo
          const timeDifference = dayjs(document.endDate).diff(dayjs(), 'seconds');

          if (timeDifference < 0) {
            document.alertType = 'expired';
          } else if (timeDifference < 2629800) { // 30 días
            document.alertType = 'critical';
          } else if (timeDifference < 5259600) { // 60 días
            document.alertType = 'warning';
          } else if (timeDifference < 15552000) { // 180 días
            document.alertType = 'ok_6_month';
          } else if (timeDifference < 31104000) { // 12 meses
            document.alertType = 'ok_12_month';
          } else if (timeDifference < 47335500) { // 18 meses
            document.alertType = 'ok_18_month';
          }
        }

        return document;
      });

      // Procesar las inspecciones válidas
      const inspectionsData = validInspections.map(inspection => {
        // Marcar como inspección para identificarlo después
        inspection.inspection = true;

        // Calcular tipo de alerta basado en la diferencia de tiempo
        if (inspection.date) {
          const timeDifference = dayjs(inspection.date).diff(dayjs(), 'seconds');

          if (timeDifference < 0) {
            inspection.alertType = 'expired';
          } else if (timeDifference < 2629800) { // 30 días
            inspection.alertType = 'critical';
          } else if (timeDifference < 5259600) { // 60 días
            inspection.alertType = 'warning';
          } else if (timeDifference < 15552000) { // 180 días
            inspection.alertType = 'ok_6_month';
          } else if (timeDifference < 31104000) { // 12 meses
            inspection.alertType = 'ok_12_month';
          } else if (timeDifference < 47335500) { // 18 meses
            inspection.alertType = 'ok_18_month';
          }
        }

        return inspection;
      });

      // Log para debugging 
      //console.log(`Documentos filtrados: ${documentsExpirations.length} → ${documentsData.length}`);
      //console.log(`Inspecciones filtradas: ${inspectionsExpirations.length} → ${inspectionsData.length}`);

      // Combinar los resultados
      return {
        documents: documentsData,
        inspections: inspectionsData
      };
    } catch (error) {
      console.error('Error en getAllExpirations:', error);
      throw error;
    }
  }
};

export default AlertsService;